import { MainMenuObject } from 'features/menu/components/menu-tile-grid/menu-tile/types';

import { MenuObjectTypes } from 'enums/menu';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { mapImageResourceToSanityImage, normalizeType } from 'state/unified-menu/utils';

type LegacyFullMenuRecoveryKind = 'item' | 'section';

const LEGACY_PRODUCT_TYPES = new Set(
  [MenuObjectTypes.ITEM, MenuObjectTypes.COMBO, MenuObjectTypes.PICKER].map(normalizeType)
);

type LegacyFullMenuRecoveryData = {
  data: MainMenuObject[];
  deeplinkUnavailableItemId: string;
  kind: LegacyFullMenuRecoveryKind;
};

export function createLegacyMenuObjectFromCatalogEntry(entry: ILocalizedMenuEntry): MainMenuObject {
  const name = entry.name?.locale ?? entry.id;
  const imageResource = entry.image?.resource;

  return ({
    _id: entry.id,
    _type: normalizeType(entry.type)?.toLowerCase(),
    name: {
      locale: name,
      localeRaw: name,
    },
    image: imageResource ? mapImageResourceToSanityImage(imageResource) : undefined,
    imageDescription: entry.image?.altText
      ? { locale: entry.image.altText.locale ?? name }
      : undefined,
  } as unknown) as MainMenuObject;
}

/**
 * Builds legacy Full Menu recovery data using the same unavailable tile chrome
 * as legacy section lists.
 */
export function buildLegacyFullMenuRecoveryData({
  availableSections,
  unavailableItem,
  unavailableSection,
}: {
  availableSections: readonly MainMenuObject[];
  unavailableItem?: MainMenuObject;
  unavailableSection?: MainMenuObject;
}): LegacyFullMenuRecoveryData | null {
  const candidate = unavailableItem ?? unavailableSection;
  if (!candidate || availableSections.length === 0) {
    return null;
  }

  const kind: LegacyFullMenuRecoveryKind = unavailableItem ? 'item' : 'section';
  const normalizedType = normalizeType(candidate._type);
  const isExpectedType =
    kind === 'section'
      ? normalizedType === normalizeType(MenuObjectTypes.SECTION)
      : LEGACY_PRODUCT_TYPES.has(normalizedType);
  if (!isExpectedType) {
    return null;
  }

  const otherSections = availableSections.filter(section => section._id !== candidate._id);
  if (otherSections.length === 0) {
    return null;
  }

  return {
    data: [candidate, ...otherSections],
    deeplinkUnavailableItemId: candidate._id,
    kind,
  };
}
